import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useConfig } from '@chainlit/react-client';

// import { OrbitAILogo } from '@/components/OrbitAILogo';
import SidebarTrigger from '@/components/header/SidebarTrigger';
import { FluidHoverGroup } from '@/components/ui/fluid-hover-group';
import {
    Sidebar,
    SidebarFooter,
    SidebarHeader,
    useSidebar
} from '@/components/ui/sidebar';

import { useDeepIntelligenceUnreadCount } from '@/hooks/DMuseDeepIntelligenceUnreadCount';
import { useIsBasicUser } from '@/hooks/useIsBasicUser';

import NewChatButton from '../header/NewChat';
import DMAgentsBtn from './DMAgentsBtn';
import DMArtifactsBtn from './DMArtifactsBtn';
import DMOrganizationArtifactsBtn from './DMOrganizationArtifactsBtn';
import DMOrganizationThreadsBtn from './DMOrganizationThreadsBtn';
import DMSidebarUserMenu from './DMSidebarFooter/DMSidebarUserMenu';
import SearchChats from './Search';
import { ThreadHistory } from './ThreadHistory';

export default function LeftSidebar({
    ...props
}: React.ComponentProps<typeof Sidebar>) {
    const navigate = useNavigate();
    const { open, openMobile, isMobile } = useSidebar();
    const isExpanded = open || (isMobile && openMobile);
    const { config } = useConfig();
    const isBasicUser = useIsBasicUser();
    // Fail-CLOSED, unlike podcast/uploadFiles (which fail-open) — Scheduled
    // Jobs defaults to off. Only visible once explicitly granted per user/
    // group/org via FeatureAccessTab (config.features.scheduled_jobs_enabled
    // === true), including in local dev without MGMT_API_URL configured.
    const scheduledJobsEnabled =
        config?.features?.scheduled_jobs_enabled === true;
    const artifactsEnabled = config?.features?.artifacts_enabled === true;
    const organizationArtifactsEnabled =
        config?.features?.organization_artifacts_enabled === true;

    const { unreadCount, refresh: refreshUnreadCount } =
        useDeepIntelligenceUnreadCount();

    useEffect(() => {
        if (scheduledJobsEnabled) refreshUnreadCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduledJobsEnabled]);

    return (
        <Sidebar
            {...props}
            className="px-0 border-border border-r! dark:border-background"
            resizable
        >
            <SidebarHeader
                className={cn(
                    'p-2 dark:border-gray-800 overflow-hidden transition-all duration-200',
                    isExpanded ? 'mb-4' : 'mb-0'
                )}
            >
                <div
                    className={cn(
                        'flex items-center w-full transition-all duration-200',
                        isExpanded
                            ? cn(
                                  'mb-3',
                                  isBasicUser
                                      ? 'justify-end'
                                      : 'justify-between'
                              )
                            : 'justify-center mb-2'
                    )}
                >
                    {isExpanded && !isBasicUser ? (
                        <div className="flex-1 mr-2 min-w-0">
                            <SearchChats showText hotkey="Cmd+K" />
                        </div>
                    ) : (
                        <span className="sr-only">Orbit AI</span>
                    )}
                    <SidebarTrigger tooltip={!isMobile} />
                </div>

                {!isExpanded && !isBasicUser && (
                    <div className="-mb-2">
                        <SearchChats showText={false} hotkey="Cmd+K" />
                    </div>
                )}

                <FluidHoverGroup
                    className={cn(
                        'flex flex-col transition-all duration-200',
                        !isExpanded && 'items-center'
                    )}
                >
                    {!isBasicUser && (
                        <NewChatButton
                            showText={isExpanded}
                            navigate={navigate}
                            hotkey="Cmd+Shift+O"
                        />
                    )}
                    {!isBasicUser && (
                        <DMOrganizationThreadsBtn
                            showText={isExpanded}
                            navigate={navigate}
                            hotkey="Cmd+Shift+G"
                        />
                    )}
                    {organizationArtifactsEnabled && (
                        <DMOrganizationArtifactsBtn
                            showText={isExpanded}
                            navigate={navigate}
                            hotkey="Cmd+Shift+E"
                        />
                    )}
                    {artifactsEnabled && !isBasicUser && (
                        <DMArtifactsBtn
                            showText={isExpanded}
                            navigate={navigate}
                            hotkey="Cmd+Shift+A"
                        />
                    )}
                    {scheduledJobsEnabled && (
                        <DMAgentsBtn
                            showText={isExpanded}
                            navigate={navigate}
                            unreadCount={unreadCount}
                            hotkey="Cmd+Shift+S"
                        />
                    )}
                </FluidHoverGroup>
            </SidebarHeader>

            {!isBasicUser && (
                <div
                    className={cn(
                        'relative flex flex-col flex-1 min-h-0',
                        !isExpanded && 'hidden'
                    )}
                >
                    <ThreadHistory />
                    <div className="right-0 bottom-0 left-0 absolute bg-linear-to-b from-transparent to-sidebar h-12 pointer-events-none" />
                </div>
            )}
            {(!isExpanded || isBasicUser) && <div className="flex-1" />}

            <SidebarFooter className="p-0 border-border border-t">
                <DMSidebarUserMenu isCollapsed={!isExpanded} />
            </SidebarFooter>
        </Sidebar>
    );
}
